import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import { Container, Table, Button, Alert, Row, Col, Nav, CardHeader, Badge, Card, Spinner, Form } from 'react-bootstrap';
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';

// Add CSS for radial dial animations
const radialDialStyles = `
  .radial-dial {
    transition: all 0.3s ease;
  }
  .radial-dial circle {
    transition: stroke-dashoffset 0.8s ease-in-out;
  }
  .radial-dial:hover {
    transform: scale(1.05);
  }
  .radial-dial:hover circle[stroke] {
    filter: brightness(1.1);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = radialDialStyles;
  document.head.appendChild(styleSheet);
}

const ResidentEvaluations = () => {
  const [assignedEvals, setAssignedEvals] = useState([]);
  const [completedEvals, setCompletedEvals] = useState([]);
  const [performanceEvals, setPerformanceEvals] = useState([]);
  const [feedbackEvals, setFeedbackEvals] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [showStats, setShowStats] = useState(() => {
    try {
      const saved = localStorage.getItem('residentEvalShowStats');
      return saved === null ? false : saved === 'true';
    } catch {
      return false;
    }
  });
  const [assignedSearch, setAssignedSearch] = useState('');
  const [assignedStatusFilter, setAssignedStatusFilter] = useState('all'); // all | overdue | active
  const [assignedTypeFilter, setAssignedTypeFilter] = useState('all'); // all | performance | feedback | survey | questionnaire | template | other
  const [completedTypeFilter, setCompletedTypeFilter] = useState('all'); // all | survey | questionnaire
  const [completedSearch, setCompletedSearch] = useState('');
  const [performanceSearch, setPerformanceSearch] = useState('');
  const [feedbackSearch, setFeedbackSearch] = useState('');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("residentEvaluationsTab") || "assigned";
  });

  // Helper function to filter out evaluations completed by directors/attendings
  const filterResidentCompletedEvaluations = (evaluations) => {
    return evaluations.filter(e => {
      // Check if the evaluator (person who completed it) is a director or attending
      // The evaluator can be in different fields: evaluator, submittedBy, or evaluatorType
      const evaluatorObj = e.evaluator || e.submittedBy;
      const evaluatorRole = evaluatorObj?.role || e.evaluatorType || (typeof evaluatorObj === 'string' ? evaluatorObj : null);
      
      // Normalize role to lowercase for comparison
      const normalizedRole = evaluatorRole?.toLowerCase();
      const isDirectorOrAttending = normalizedRole === 'director' || normalizedRole === 'attending';
      
    // Debug logging removed
      
      // If evaluator is director or attending, this was completed by them, not by the resident
      // Exclude it from the statistics
      if (isDirectorOrAttending) {
        return false;
      }
      
      // Otherwise, include it (either no evaluator or evaluator is not director/attending)
      return true;
    });
  };

  const getEvaluationIcon = (type) => {
    const t = (type || '').toLowerCase();
    if (t === 'performance') return <i className="bi bi-bar-chart-fill text-primary" />;
    if (t === 'feedback') return <i className="bi bi-chat-dots-fill text-info" />;
    if (t === 'survey') return <i className="bi bi-clipboard-check-fill text-success" />;
    if (t === 'questionnaire') return <i className="bi bi-patch-question-fill text-warning" />;
    return <i className="bi bi-clipboard-data text-muted" />;
  };

  const getEvaluationIconBg = (type) => {
    const t = (type || '').toLowerCase();
    if (t === 'performance') return 'var(--blue-light)';
    if (t === 'feedback') return 'var(--indigo-light)';
    if (t === 'survey') return 'var(--green-light)';
    if (t === 'questionnaire') return 'var(--yellow-light)';
    return 'var(--gray-100)';
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem("residentEvaluationsTab", tab);
  };

  useEffect(() => {
    try {
      localStorage.setItem('residentEvalShowStats', String(showStats));
    } catch {}
  }, [showStats]);

  useEffect(() => {
    const fetchEvaluations = async () => {
      try {
        // console.log('Starting to fetch evaluations...');
        const token = localStorage.getItem("token");
        if (!token) {
          // console.log("No token found, redirecting to login");
          navigate("/");
          return;
        }

        // Decode token to get user role
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const role = tokenPayload.role;
        setUserRole(role);
        // console.log('User role:', role);

        // Fetch assigned evaluations
        // console.log('Fetching assigned evaluations...');
        const assignedRes = await fetch(`${API_ENDPOINTS.RESIDENT}/assigned`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        });

        if (!assignedRes.ok) {
          if (assignedRes.status === 401) {
            // console.log("Token expired or invalid, redirecting to login");
            localStorage.removeItem("token");
            localStorage.removeItem("notifications");
            localStorage.removeItem("lastNotificationTime");
            window.dispatchEvent(new CustomEvent('app-logout'));
            navigate("/");
            return;
          }
          if (assignedRes.status === 403) {
            const errorData = await assignedRes.json();
            // console.log("Permission denied:", errorData);
            setError("You don't have permission to access this resource. Please contact your program director.");
            return;
          }
          throw new Error(`HTTP error! status: ${assignedRes.status}`);
        }

        const assignedData = await assignedRes.json();
        // console.log('Assigned evaluations:', assignedData);
        // console.log('First evaluation:', assignedData[0]);
        // console.log('First evaluation progress:', assignedData[0]?.progress);
        setAssignedEvals(assignedData);

        // Fetch completed evaluations
        // console.log('Fetching completed evaluations...');
        const completedRes = await fetch(`${API_ENDPOINTS.RESIDENT}/completed`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        });

        if (!completedRes.ok) {
          if (completedRes.status === 403) {
            const errorData = await completedRes.json();
            // console.log("Permission denied:", errorData);
            setError("You don't have permission to access this resource. Please contact your program director.");
            return;
          }
          throw new Error(`HTTP error! status: ${completedRes.status}`);
        }

        const completedData = await completedRes.json();
        // console.log('Completed evaluations raw data:', completedData);

        // Fetch template information for each evaluation
        const completedEvalsWithTemplates = await Promise.all(
          completedData.map(async (evaluation) => {
            try {
              const templateRes = await fetch(`${API_ENDPOINTS.PROTECTED}/templates/${evaluation.templateId}`, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              if (templateRes.ok) {
                const templateData = await templateRes.json();
                
                return {
                  ...evaluation,
                  template: templateData,
                  evaluator: evaluation.submittedBy, // Map submittedBy to evaluator
                  // Preserve original endTime from API, or use template data if available
                  endTime: evaluation.endTime || templateData?.endTime || templateData?.dueDate,
                  dueDate: templateData?.dueDate || templateData?.endTime || evaluation.endTime
                };
              } else {
                
              }
              return {
                ...evaluation,
                evaluator: evaluation.submittedBy, // Map submittedBy to evaluator
                // Preserve original endTime from API response
                endTime: evaluation.endTime || evaluation.template?.endTime || evaluation.template?.dueDate,
                dueDate: evaluation.template?.dueDate || evaluation.template?.endTime || evaluation.endTime
              };
            } catch (err) {
              // console.error('Error fetching template:', err);
              return {
                ...evaluation,
                evaluator: evaluation.submittedBy, // Map submittedBy to evaluator
                // Preserve original endTime from API response
                endTime: evaluation.endTime || evaluation.template?.endTime || evaluation.template?.dueDate,
                dueDate: evaluation.template?.dueDate || evaluation.template?.endTime || evaluation.endTime
              };
            }
          })
        );

        // console.log('Completed evaluations with templates:', completedEvalsWithTemplates);
        setCompletedEvals(completedEvalsWithTemplates);

        // Only fetch performance and feedback evaluations if user is a resident
        if (userRole === 'resident') {
          // Fetch performance evaluations
          // console.log('Fetching performance evaluations for resident...');
          try {
            const performanceRes = await fetch(`${API_ENDPOINTS.RESIDENT}/performance-evaluations`, {
              method: 'GET',
              headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (performanceRes.ok) {
              const performanceData = await performanceRes.json();
              // console.log('Raw performance data structure:', JSON.stringify(performanceData, null, 2));
              // Filter evaluations that have been completed
              const validPerformanceEvals = performanceData.filter(evaluation => {
                // console.log('Processing performance evaluation:', evaluation);
                return evaluation && 
                       evaluation.evaluation && 
                       evaluation.evaluation.template &&
                       evaluation.evaluation.status === 'completed';
              });
              // console.log('Valid performance evaluations:', validPerformanceEvals);
              setPerformanceEvals(validPerformanceEvals);
            } else if (performanceRes.status === 403) {
              // console.log('No permission to access performance evaluations - this is expected for residents');
              setPerformanceEvals([]);
            } else {
              // console.error('Error fetching performance evaluations:', performanceRes.status);
              setPerformanceEvals([]);
            }
          } catch (err) {
            // console.error('Error fetching performance evaluations:', err);
            setPerformanceEvals([]);
          }

          // Fetch feedback evaluations
          // console.log('Fetching feedback evaluations for resident...');
          try {
            const feedbackRes = await fetch(`${API_ENDPOINTS.RESIDENT}/feedback-evaluations`, {
              method: 'GET',
              headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (feedbackRes.ok) {
              const feedbackData = await feedbackRes.json();
              // console.log('Raw feedback data structure:', JSON.stringify(feedbackData, null, 2));
              // Filter evaluations that have been completed
              const validFeedbackEvals = feedbackData.filter(evaluation => {
                // console.log('Processing feedback evaluation:', evaluation);
                return evaluation && 
                       evaluation.responses && 
                       evaluation.responses.length > 0 &&
                       evaluation.status === 'completed';
              });
              // console.log('Valid feedback evaluations:', validFeedbackEvals);
              setFeedbackEvals(validFeedbackEvals);
            } else if (feedbackRes.status === 403) {
              // console.log('No permission to access feedback evaluations - this is expected for residents');
              setFeedbackEvals([]);
            } else {
              // console.error('Error fetching feedback evaluations:', feedbackRes.status);
              setFeedbackEvals([]);
            }
          } catch (err) {
            // console.error('Error fetching feedback evaluations:', err);
            setFeedbackEvals([]);
          }
        } else {
          // If not a resident, set empty arrays
          // console.log('User is not a resident - setting empty arrays for performance and feedback evaluations');
          setPerformanceEvals([]);
          setFeedbackEvals([]);
        }

        // console.log('All evaluations fetched successfully');
        setLoading(false);
      } catch (err) {
        // console.error("Error fetching evaluations:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchEvaluations();
  }, [navigate]);
 
  // Derived helpers and filters for Assigned tab
  const now = new Date();

  const getEvaluationTypeKey = (evaluation) => {
    return (evaluation.template?.type || evaluation.evaluationType || '').toLowerCase();
  };

  const isEvaluationOverdue = (evaluation) => {
    const end = evaluation.endTime || evaluation.template?.endTime;
    if (!end) return false;
    const endDate = new Date(end);
    if (isNaN(endDate.getTime())) return false;
    return endDate < now;
  };

  const isEvaluationActive = (evaluation) => {
    const end = evaluation.endTime || evaluation.template?.endTime;
    if (!end) return false;
    const endDate = new Date(end);
    if (isNaN(endDate.getTime())) return false;
    return endDate >= now;
  };

  const overdueAssignedEvals = assignedEvals.filter(isEvaluationOverdue);
  const activeAssignedEvals = assignedEvals.filter(isEvaluationActive);

  const assignedTypeCounts = assignedEvals.reduce((acc, evaluation) => {
    const key = getEvaluationTypeKey(evaluation) || 'other';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const filteredAssignedEvals = assignedEvals.filter((evaluation) => {
    // Status filter
    if (assignedStatusFilter === 'overdue' && !isEvaluationOverdue(evaluation)) {
      return false;
    }
    if (assignedStatusFilter === 'active' && !isEvaluationActive(evaluation)) {
      return false;
    }

    // Type filter
    const typeKey = getEvaluationTypeKey(evaluation) || 'other';
    if (assignedTypeFilter !== 'all' && typeKey !== assignedTypeFilter) {
      return false;
    }

    // Search filter
    if (assignedSearch.trim()) {
      const title = evaluation.title || '';
      if (!title.toLowerCase().includes(assignedSearch.toLowerCase())) {
        return false;
      }
    }

    return true;
  });

  // Derived data for Completed tab (survey + questionnaire)
  const completedTabEvals = completedEvals.filter(e =>
    e.evaluationType === 'survey' || e.evaluationType === 'questionnaire'
  );
  const completedTypeCounts = completedTabEvals.reduce((acc, e) => {
    const key = e.evaluationType || 'other';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const filteredCompletedEvals = completedTabEvals.filter((e) => {
    if (completedTypeFilter !== 'all' && (e.evaluationType || '') !== completedTypeFilter) return false;
    if (completedSearch.trim()) {
      const title = e.title || '';
      if (!title.toLowerCase().includes(completedSearch.toLowerCase())) return false;
    }
    return true;
  });

  // Derived data for Performance tab
  const performanceTabEvals = completedEvals.filter(e => e.evaluationType === 'performance');
  const filteredPerformanceEvals = performanceTabEvals.filter((e) => {
    if (performanceSearch.trim()) {
      const title = e.title || '';
      const evaluatorName = e.submittedBy ? `${e.submittedBy.firstName || ''} ${e.submittedBy.lastName || ''}`.trim() :
        e.evaluator ? `${e.evaluator.firstName || ''} ${e.evaluator.lastName || ''}`.trim() : '';
      const search = performanceSearch.toLowerCase();
      if (!title.toLowerCase().includes(search) && !evaluatorName.toLowerCase().includes(search)) return false;
    }
    return true;
  });

  // Derived data for Feedback tab
  const feedbackTabEvals = completedEvals.filter(e => e.evaluationType === 'feedback');
  const filteredFeedbackEvals = feedbackTabEvals.filter((e) => {
    if (feedbackSearch.trim()) {
      const title = e.title || '';
      const evaluatorName = e.submittedBy ? `${e.submittedBy.firstName || ''} ${e.submittedBy.lastName || ''}`.trim() :
        e.evaluator ? `${e.evaluator.firstName || ''} ${e.evaluator.lastName || ''}`.trim() : '';
      const search = feedbackSearch.toLowerCase();
      if (!title.toLowerCase().includes(search) && !evaluatorName.toLowerCase().includes(search)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div>
        <PageTitle title="Loading..." />
        <Navbar pageTitle="My Evaluations" />
        <div className="bg-white shadow-sm">
        <Container>
          <Row>
            <Col lg={2} md={2} sm={12} className=" d-flex align-items-center justify-content-start py-md-0 py-3">
              <h6 className="mb-0 fw-semibold">Evaluations</h6>
            </Col>
            <Col lg={10} md={10} sm={12} className="d-flex align-items-center justify-content-start">
              <Nav variant="tabs" style={{marginBottom: '-1px'}} activeKey={activeTab} onSelect={handleTabChange}>
              <Nav.Item>
                  <Nav.Link eventKey="assigned" active={activeTab === 'assigned'}>Assigned</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="completed" active={activeTab === 'completed'}>Completed</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="performance" active={activeTab === 'performance'}>Performance</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="feedback" active={activeTab === 'feedback'}>Feedback</Nav.Link>
              </Nav.Item>
            </Nav>
            </Col>
          </Row>
        </Container>
         </div>        
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <Spinner 
            animation="border" 
            variant="primary" 
            size="lg"
            className="mb-3"
          />
          <div className="text-muted">Loading evaluations...</div>
        </div>
      </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageTitle title="Evaluation Error" />
        <Navbar pageTitle="My Evaluations" />
        <Container className="py-4">
          <Alert variant="danger">Error: {error}</Alert>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="My Evaluations" description="View and complete your assigned evaluations" />
      <Navbar pageTitle="My Evaluations" />
      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <Row>
            <Col lg={2} md={2} sm={12} className=" d-flex align-items-center justify-content-start py-md-0 py-3">
              <h6 className="mb-0 fw-semibold">Evaluations</h6>
            </Col>
            <Col lg={10} md={10} sm={12} className="d-flex align-items-center justify-content-between">
              <Nav variant="tabs" className="scrollable-tabs" style={{marginBottom: '-1px'}} activeKey={activeTab} onSelect={handleTabChange}>
              <Nav.Item>
                  <Nav.Link eventKey="assigned" active={activeTab === 'assigned'}>Assigned</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="completed" active={activeTab === 'completed'}>Completed</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="performance" active={activeTab === 'performance'}>Performance</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="feedback" active={activeTab === 'feedback'}>Feedback</Nav.Link>
              </Nav.Item>
            </Nav>
            {activeTab === 'assigned' && (
                            <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setShowStats(prev => !prev)}
                            className="d-flex align-items-center gap-2"
                          >
                            <i className={`bi ${showStats ? 'bi-eye-slash' : 'bi-bar-chart'}`}></i>
                            {showStats ? 'Hide Stats' : 'View Stats'}
                          </Button>
            )}
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="mt-4">
        {activeTab === 'assigned' && (
          <>
             
            {/* Modern Radial Dial Statistics - Conditionally Rendered */}
            {showStats && (
              <div className="mt-3 mb-3">
              <Row className="g-3 h-100">
                {/* Total Evaluations by Type Pie Chart */}
                <Col lg={3} md={4} xs={12}>
                <Card className="shadow-sm border-0 h-100">
                  <CardHeader className="border-0 px-4 py-3">
                    <h6 className="mb-0 fw-semibold">Total Evaluations</h6>
                  </CardHeader>
                  <Card.Body className="p-0">
                  <div className="d-flex flex-row align-items-center justify-content-center gap-3 py-3">
                    <div className="position-relative mb-3">
                      <svg width="120" height="120" viewBox="0 0 120 120" className="radial-dial">
                        {(() => {
                          const allEvals = [...assignedEvals, ...completedEvals];
                          const performanceEvals = allEvals.filter(e => 
                            e.template?.type?.toLowerCase() === 'performance' || 
                            e.evaluationType?.toLowerCase() === 'performance'
                          );
                          const feedbackEvals = allEvals.filter(e => 
                            e.template?.type?.toLowerCase() === 'feedback' || 
                            e.evaluationType?.toLowerCase() === 'feedback'
                          );
                          const surveyEvals = allEvals.filter(e => 
                            e.template?.type?.toLowerCase() === 'survey' || 
                            e.evaluationType?.toLowerCase() === 'survey'
                          );
                          const questionnaireEvals = allEvals.filter(e => 
                            e.template?.type?.toLowerCase() === 'questionnaire' || 
                            e.evaluationType?.toLowerCase() === 'questionnaire'
                          );
                          const otherEvals = allEvals.filter(e => {
                            const type = e.template?.type?.toLowerCase() || e.evaluationType?.toLowerCase();
                            return type && !['performance', 'feedback', 'survey', 'questionnaire'].includes(type);
                          });
                          
                          const total = allEvals.length;
                          if (total === 0) return null;
                          
                          const radius = 50;
                          const innerRadius = 30;
                          const centerX = 60;
                          const centerY = 60;
                          
                          // Calculate angles for each segment to ensure proper connection
                          const performanceAngle = (performanceEvals.length / total) * 360;
                          const feedbackAngle = (feedbackEvals.length / total) * 360;
                          const surveyAngle = (surveyEvals.length / total) * 360;
                          const questionnaireAngle = (questionnaireEvals.length / total) * 360;
                          const otherAngle = (otherEvals.length / total) * 360;
                          
                          let currentAngle = 0;
                          
                          const createSegmentPath = (startAngle, sweepAngle, fillColor) => {
                            if (sweepAngle <= 0) return null;
                            
                            const startAngleRad = (startAngle - 90) * Math.PI / 180;
                            const endAngleRad = (startAngle + sweepAngle - 90) * Math.PI / 180;
                            
                            const x1 = centerX + radius * Math.cos(startAngleRad);
                            const y1 = centerY + radius * Math.sin(startAngleRad);
                            const x2 = centerX + radius * Math.cos(endAngleRad);
                            const y2 = centerY + radius * Math.sin(endAngleRad);
                            const x3 = centerX + innerRadius * Math.cos(endAngleRad);
                            const y3 = centerY + innerRadius * Math.sin(endAngleRad);
                            const x4 = centerX + innerRadius * Math.cos(startAngleRad);
                            const y4 = centerY + innerRadius * Math.sin(startAngleRad);
                            
                            const largeArcFlag = sweepAngle > 180 ? 1 : 0;
                            
                            return (
                              <path
                                key={fillColor}
                                d={`M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4} Z`}
                                fill={fillColor}
                              />
                            );
                          };
                          
                          return (
                            <>
                              {/* Performance segment */}
                              {createSegmentPath(currentAngle, performanceAngle, "var(--bs-primary)")}
                              {currentAngle += performanceAngle}
                              
                              {/* Feedback segment */}
                              {createSegmentPath(currentAngle, feedbackAngle, "var(--bs-info)")}
                              {currentAngle += feedbackAngle}
                              
                              {/* Survey segment */}
                              {createSegmentPath(currentAngle, surveyAngle, "var(--bs-success)")}
                              {currentAngle += surveyAngle}
                              
                              {/* Questionnaire segment */}
                              {createSegmentPath(currentAngle, questionnaireAngle, "var(--bs-warning)")}
                              {currentAngle += questionnaireAngle}
                              
                              {/* Other segment */}
                              {createSegmentPath(currentAngle, otherAngle, "var(--gray-300)")}
                            </>
                          );
                        })()}
                        
                        <text x="60" y="70" textAnchor="middle" className="fs-4 fw-bold" fill="var(--gray-800)">
                          {(() => {
                            // Include all evaluations including those by directors
                            return assignedEvals.length + completedEvals.length;
                          })()}
                        </text>
                      </svg>
                    </div>
                      
                      {/* Evaluation Type Legend */}
                      <div className="d-flex flex-row align-items-center justify-content-between gap-3">
                      <div className="mb-2">
                        <div className="d-flex flex-column justify-content-start gap-2">
                          {(() => {
                            const allEvals = [...assignedEvals, ...completedEvals];
                            const performanceEvals = allEvals.filter(e => 
                              e.template?.type?.toLowerCase() === 'performance' || 
                              e.evaluationType?.toLowerCase() === 'performance'
                            );
                            const feedbackEvals = allEvals.filter(e => 
                              e.template?.type?.toLowerCase() === 'feedback' || 
                              e.evaluationType?.toLowerCase() === 'feedback'
                            );
                            const surveyEvals = allEvals.filter(e => 
                              e.template?.type?.toLowerCase() === 'survey' || 
                              e.evaluationType?.toLowerCase() === 'survey'
                            );
                            const questionnaireEvals = allEvals.filter(e => 
                              e.template?.type?.toLowerCase() === 'questionnaire' || 
                              e.evaluationType?.toLowerCase() === 'questionnaire'
                            );
                            const otherEvals = allEvals.filter(e => {
                              const type = e.template?.type?.toLowerCase() || e.evaluationType?.toLowerCase();
                              return type && !['performance', 'feedback', 'survey', 'questionnaire'].includes(type);
                            });
                            
                            const legends = [];
                            if (performanceEvals.length > 0) {
                              legends.push(
                                <div key="performance" className="d-flex align-items-center">
                                  <div className="me-1" style={{width: '8px', height: '8px', backgroundColor: 'var(--bs-primary)', borderRadius: '50%'}}></div>
                                  <span className="small">{performanceEvals.length} Performance</span>
                  </div>
                              );
                            }
                            if (feedbackEvals.length > 0) {
                              legends.push(
                                <div key="feedback" className="d-flex align-items-center">
                                  <div className="me-1" style={{width: '8px', height: '8px', backgroundColor: 'var(--bs-info)', borderRadius: '50%'}}></div>
                                  <span className="small">{feedbackEvals.length} Feedback</span>
                      </div>
                              );
                            }
                            if (surveyEvals.length > 0) {
                              legends.push(
                                <div key="survey" className="d-flex align-items-center">
                                  <div className="me-1" style={{width: '8px', height: '8px', backgroundColor: 'var(--bs-success)', borderRadius: '50%'}}></div>
                                  <span className="small">{surveyEvals.length} Survey</span>
                    </div>
                              );
                            }
                            if (questionnaireEvals.length > 0) {
                              legends.push(
                                <div key="questionnaire" className="d-flex align-items-center">
                                  <div className="me-1" style={{width: '8px', height: '8px', backgroundColor: 'var(--bs-warning)', borderRadius: '50%'}}></div>
                                  <span className="small">{questionnaireEvals.length} Questionnaire</span>
                  </div>
                              );
                            }
                            if (otherEvals.length > 0) {
                              legends.push(
                                <div key="other" className="d-flex align-items-center">
                                  <div className="me-1" style={{width: '8px', height: '8px', backgroundColor: 'var(--bs-secondary)', borderRadius: '50%'}}></div>
                                  <span className="small">{otherEvals.length} Other</span>
                      </div>
                              );
                            }
                            return legends;
                          })()}
                    </div>
                  </div>
                      
                  
                    </div>
                  </div>
                  </Card.Body>
                  </Card>
                </Col>

    
                 {/* Multi-Stroke Status Dial - Including Completed Evaluations */}
                <Col lg={3} md={4} xs={12}>
                <Card className="shadow-sm border-0 h-100">
                  <CardHeader className="border-0 px-4 py-3">
                    <h6 className="mb-0 fw-semibold">Assigned Status Breakdown</h6>
                  </CardHeader>
                  <Card.Body className="p-0">
                  <div className="d-flex flex-row align-items-center justify-content-center gap-3 py-3">
                    <div className="position-relative mb-3">
                      <svg width="120" height="120" viewBox="0 0 120 120" className="radial-dial">
                        <circle cx="60" cy="60" r="50" fill="none" stroke="var(--bs-gray-200)" strokeWidth="8"/>
                        {(() => {
                          const completedFromAssigned = assignedEvals.filter(
                              e => e.status && (e.status.toLowerCase() === "completed" || e.status.toLowerCase() === "submitted" || e.status.toLowerCase() === "done")
                            );
                          const residentCompletedFromAssigned = filterResidentCompletedEvaluations(completedFromAssigned);
                          const residentCompletedEvals = filterResidentCompletedEvaluations(completedEvals);
                          const totalCompleted = residentCompletedFromAssigned.length + residentCompletedEvals.length;
                          const pending = assignedEvals.filter(
                            e => !e.status || (e.status && (e.status.toLowerCase() === "pending" || e.status === "incomplete" || e.status === "assigned"))
                          );
                          const overdue = assignedEvals.filter(
                            e => (!e.status || (e.status && (e.status.toLowerCase() === "pending" || e.status === "incomplete" || e.status === "assigned"))) && (new Date() > new Date(e.endTime))
                          );
                          
                          const total = assignedEvals.length + residentCompletedEvals.length;
                          const circumference = 2 * Math.PI * 60;
                          let currentOffset = 0;
                          
                          return (
                            <>
                              {/* Completed segment */}
                              <circle 
                                cx="60" 
                                cy="60" 
                                r="50" 
                                fill="none" 
                                stroke="var(--bs-success)" 
                                strokeWidth="8" 
                                strokeDasharray={`${(totalCompleted / total) * circumference} ${circumference}`} 
                                strokeDashoffset={-currentOffset}
                                strokeLinecap="round"
                                transform="rotate(-90 60 60)"
                              />
                              {currentOffset += (totalCompleted / total) * circumference}
                              
                              {/* Pending segment */}
                              <circle 
                                cx="60" 
                                cy="60" 
                                r="50" 
                                fill="none" 
                                stroke="var(--bs-warning)" 
                                strokeWidth="8" 
                                strokeDasharray={`${(pending.length / total) * circumference} ${circumference}`} 
                                strokeDashoffset={-currentOffset}
                                strokeLinecap="round"
                                transform="rotate(-90 60 60)"
                              />
                              {currentOffset += (pending.length / total) * circumference}
                              
                              {/* Overdue segment */}
                              <circle 
                                cx="60" 
                                cy="60" 
                                r="50" 
                                fill="none" 
                                stroke="var(--bs-danger)" 
                                strokeWidth="8" 
                                strokeDasharray={`${(overdue.length / total) * circumference} ${circumference}`} 
                                strokeDashoffset={-currentOffset}
                                strokeLinecap="round"
                                transform="rotate(-90 60 60)"
                              />
                            </>
                          );
                        })()}
                        <text x="58" y="63" textAnchor="middle" className="fs-4 fw-bold" fill="var(--gray-800)">
                          {(() => {
                            const completedFromAssigned = assignedEvals.filter(
                              e => e.status && (e.status.toLowerCase() === "completed" || e.status.toLowerCase() === "submitted" || e.status.toLowerCase() === "done")
                            );
                            const residentCompletedFromAssigned = filterResidentCompletedEvaluations(completedFromAssigned);
                            const residentCompletedEvals = filterResidentCompletedEvaluations(completedEvals);
                            return assignedEvals.length + residentCompletedEvals.length;
                          })()}
                        </text>
                        <text x="58" y="78" textAnchor="middle" fill="var(--gray-600)" className="small">
                          Total
                        </text>
                      </svg>
                      </div>
                    
                      <div className="d-flex flex-column  justify-content-center gap-2">
                        <div className="d-flex align-items-center">
                          <div className="me-2" style={{width: '8px', height: '8px', backgroundColor: 'var(--bs-success)', borderRadius: '50%'}}></div>
                          <span className="small text-muted">
                            {(() => {
                              const completedFromAssigned = assignedEvals.filter(e => e.status && (e.status.toLowerCase() === "completed" || e.status.toLowerCase() === "submitted" || e.status.toLowerCase() === "done"));
                              const residentCompletedFromAssigned = filterResidentCompletedEvaluations(completedFromAssigned);
                              const residentCompletedEvals = filterResidentCompletedEvaluations(completedEvals);
                              return residentCompletedFromAssigned.length + residentCompletedEvals.length;
                            })()} Completed
                          </span>
                    </div>
                        <div className="d-flex align-items-center">
                          <div className="me-2" style={{width: '8px', height: '8px', backgroundColor: 'var(--bs-warning)', borderRadius: '50%'}}></div>
                          <span className="small text-muted">
                            {assignedEvals.filter(e => !e.status || (e.status && (e.status.toLowerCase() === "pending" || e.status === "incomplete" || e.status === "assigned"))).length} Pending
                          </span>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="me-2" style={{width: '8px', height: '8px', backgroundColor: 'var(--bs-danger)', borderRadius: '50%'}}></div>
                          <span className="small text-muted">
                            {assignedEvals.filter(e => (!e.status || (e.status && (e.status.toLowerCase() === "pending" || e.status === "incomplete" || e.status === "assigned"))) && (new Date() > new Date(e.endTime))).length} Overdue
                          </span>
                        </div>
                    </div>
                  </div>
                  </Card.Body>
                  </Card>
                </Col>

                {/* Completion Rate Radial Dial - Based on All Evaluations */}
                <Col lg={3} md={4} xs={12}>
                  <Card className="shadow-sm border-0 h-100">
                    <CardHeader className="border-0 px-4 py-3">
                      <h6 className="mb-0 fw-semibold">Completion Rate</h6>
                    </CardHeader>
                    <Card.Body className="p-0">
                  <div className="d-flex flex-column align-items-center p-3">
                    <div className="position-relative mb-3">
                      <svg width="120" height="120" viewBox="0 0 120 120" className="radial-dial">
                        <circle cx="60" cy="60" r="50" fill="none" stroke="var(--gray-300)" strokeWidth="8"/>
                        {(() => {
                          const completedFromAssigned = assignedEvals.filter(
                              e => e.status && (e.status.toLowerCase() === "completed" || e.status.toLowerCase() === "submitted" || e.status.toLowerCase() === "done")
                            );
                          const residentCompletedFromAssigned = filterResidentCompletedEvaluations(completedFromAssigned);
                          const residentCompletedEvals = filterResidentCompletedEvaluations(completedEvals);
                          const totalCompleted = residentCompletedFromAssigned.length + residentCompletedEvals.length;
                          const totalEvals = assignedEvals.length + residentCompletedEvals.length;
                          const completionRate = totalEvals > 0 ? totalCompleted / totalEvals : 0;
                          const circumference = 2 * Math.PI * 50;
                          const strokeDashoffset = circumference * (1 - completionRate);
                          
                          return (
                            <circle 
                              cx="60" 
                              cy="60" 
                              r="50" 
                              fill="none" 
                              stroke="var(--bs-success)" 
                              strokeWidth="8" 
                              strokeDasharray={circumference} 
                              strokeDashoffset={strokeDashoffset}
                              strokeLinecap="round"
                              transform="rotate(-90 60 60)"
                            />
                          );
                        })()}
                        <text x="60" y="70" textAnchor="middle" fill="var(--gray-800)" className="fs-4 fw-bold">
                          {(() => {
                            const completedFromAssigned = assignedEvals.filter(
                              e => e.status && (e.status.toLowerCase() === "completed" || e.status.toLowerCase() === "submitted" || e.status.toLowerCase() === "done")
                            );
                            const residentCompletedFromAssigned = filterResidentCompletedEvaluations(completedFromAssigned);
                            const residentCompletedEvals = filterResidentCompletedEvaluations(completedEvals);
                            const totalCompleted = residentCompletedFromAssigned.length + residentCompletedEvals.length;
                            const totalEvals = assignedEvals.length + residentCompletedEvals.length;
                            if (totalEvals === 0) return "0%";
                            return `${Math.round((totalCompleted / totalEvals) * 100)}%`;
                          })()}
                        </text>
                      </svg>
                      </div>
                      </div>
                  </Card.Body>
                  </Card>
                </Col>

               
             
              <Col lg={3} md={12} xs={12}>
              {/* Additional Metrics Row */}

             
                  <Card className="shadow-sm border-0 h-100">
                    <CardHeader className="border-0 px-4 py-3">
                      <h6 className="mb-0 fw-semibold">Completion Metrics</h6>
                    </CardHeader>
                    <Card.Body className="p-3 border-0">
                      {/* Modern, simple metrics with progress bars */}
                      {(() => {
                        const residentCompletedEvals = filterResidentCompletedEvaluations(completedEvals);

                        // Completed On Time
                        const completedOnTime = residentCompletedEvals.filter(e => {
                          const submittedAt = e.submittedAt;
                          const endTime = e.endTime || e.template?.endTime;
                          if (!submittedAt || !endTime) return false;
                          const submittedDate = new Date(submittedAt);
                          const endDate = new Date(endTime);
                          if (isNaN(submittedDate.getTime()) || isNaN(endDate.getTime())) return false;
                          return submittedDate <= endDate;
                        });

                        // Completed Past Due
                        const completedPastDue = residentCompletedEvals.filter(e => {
                          const submittedAt = e.submittedAt;
                          const endTime = e.endTime || e.template?.endTime;
                          if (!submittedAt || !endTime) return false;
                          const submittedDate = new Date(submittedAt);
                          const endDate = new Date(endTime);
                          if (isNaN(submittedDate.getTime()) || isNaN(endDate.getTime())) return false;
                          return submittedDate > endDate;
                        });

                        // Average Time to Complete
                        const completedWithTimes = residentCompletedEvals.filter(e => {
                          const completionTime = e.submittedAt || e.completionTime || e.completedAt || e.updatedAt || e.createdAt;
                          const startTime = e.startTime;
                          return completionTime && startTime;
                        });
                        let avgTime = "-";
                        if (completedWithTimes.length > 0) {
                          const times = completedWithTimes.map(e => {
                            const completedTime = new Date(e.completionTime || e.updatedAt || e.completedAt || e.submittedAt || e.createdAt);
                            const assignedTime = new Date(e.startTime);
                            return (completedTime - assignedTime) / (1000 * 60 * 60 * 24);
                          }).filter(v => !isNaN(v) && v > 0);
                          if (times.length > 0) {
                            const avg = times.reduce((a, b) => a + b, 0) / times.length;
                            avgTime = `${avg.toFixed(1)}d`;
                          }
                        }

                        // For percentages
                        const total = residentCompletedEvals.length || 1; // 1 prevents divide by zero for 0 case
                        const onTimePercent = Math.round((completedOnTime.length / total) * 100);
                        const pastDuePercent = Math.round((completedPastDue.length / total) * 100);

                        return (
                          <div className="d-flex flex-column gap-3">
                            {/* Completed On Time */}
                            <div>
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-semibold small">On Time</span>
                                <div><span className="fw-bold">{completedOnTime.length}</span> <span className="text-muted small">of {total}</span></div>
                              </div>
                              <div className="progress" style={{ height: "8px" }}>
                                <div
                                  className="progress-bar bg-success-solid"
                                  role="progressbar"
                                  style={{ width: `${onTimePercent}%` }}
                                  aria-valuenow={onTimePercent}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                />
                              </div>
                            </div>

                            {/* Completed Past Due */}
                            <div>
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-semibold small">Past Due</span>
                                <div><span className="fw-bold">{completedPastDue.length}</span> <span className="text-muted small">of {total}</span></div>
                              </div>
                              <div className="progress" style={{ height: "8px" }}>
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                    style={{ width: `${pastDuePercent}%`, background: 'linear-gradient(90deg, var(--red) 0%,rgb(218, 24, 121) 100%)'}}
                                    aria-valuenow={pastDuePercent}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                />
                              </div>
                            </div>

                            {/* Average time to complete */}
                            <div>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-semibold small">Avg Time to Complete</span>
                                <div><span className="fw-bold">{avgTime}</span> <span className="text-muted small">days</span></div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
            )}
                <Row className="g-3">
                  {/* Left filters column */}
                  <Col lg={2} md={3} className="d-none d-md-block">
                    <Card className="shadow-sm border-0 mb-3 custom-sticky-top" style={{ top: '5rem' }}>
                      <CardHeader className="border-0 px-3 py-3">
                        <h6 className="mb-0 fw-semibold">Status</h6>
                        </CardHeader>
                        <Card.Body className="pe-0 ps-2 pt-0 pb-3">
                        <Nav variant="pills" className="flex-column small">
                          <Nav.Item>
                            <Nav.Link
                              active={assignedStatusFilter === 'all'}
                              onClick={() => setAssignedStatusFilter('all')}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <span>All</span>
                              <span className="badge bg-light ms-2">{assignedEvals.length}</span>
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link
                              active={assignedStatusFilter === 'overdue'}
                              onClick={() => setAssignedStatusFilter('overdue')}
                              disabled={overdueAssignedEvals.length === 0}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <span className="d-flex flex-row align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--bs-danger)', borderRadius: '50%'}}></div> Overdue</span>
                              <span className="badge bg-light ms-2">{overdueAssignedEvals.length}</span>
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link
                              active={assignedStatusFilter === 'active'}
                              onClick={() => setAssignedStatusFilter('active')}
                              disabled={activeAssignedEvals.length === 0}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <span className="d-flex flex-row align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--bs-info)', borderRadius: '50%'}}></div> Active</span>
                              <span className="badge bg-light ms-2">{activeAssignedEvals.length}</span>
                            </Nav.Link>
                          </Nav.Item>
                        </Nav>
                      </Card.Body>
   
                        <CardHeader className="border-0 px-3 py-3">
                          <h6 className="mb-0 fw-semibold">Type</h6>
                        </CardHeader>
                        <Card.Body className="pe-0 ps-2 pt-0 pb-3">
                        <Nav variant="pills" className="flex-column ">
                          <Nav.Item>
                            <Nav.Link
                              active={assignedTypeFilter === 'all'}
                              onClick={() => setAssignedTypeFilter('all')}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <span>All Types</span>
                              <span className="badge bg-light ms-2">{assignedEvals.length}</span>
                            </Nav.Link>
                          </Nav.Item>
                          {['performance', 'feedback', 'survey', 'questionnaire', 'template', 'other'].map((typeKey) => {
                            const count = assignedTypeCounts[typeKey] || 0;
                            if (!count) return null;
                            const typeColorMap = {
                              performance: 'var(--bs-primary)',
                              feedback: 'var(--bs-info)',
                              survey: 'var(--bs-success)',
                              questionnaire: 'var(--bs-warning)',
                              template: 'var(--bs-secondary)',
                              other: 'var(--gray-300)'
                            };
                            const label =
                              typeKey === 'performance'
                                ? 'Performance'
                                : typeKey === 'feedback'
                                ? 'Feedback'
                                : typeKey === 'survey'
                                ? 'Survey'
                                : typeKey === 'questionnaire'
                                ? 'Questionnaire'
                                : typeKey === 'template'
                                ? 'Template'
                                : 'Other';
                            return (
                              <Nav.Item key={typeKey}>
                                <Nav.Link
                                  active={assignedTypeFilter === typeKey}
                                  onClick={() => setAssignedTypeFilter(typeKey)}
                                  className="d-flex justify-content-between align-items-center"
                                >
                                  <span className="d-flex flex-row align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: typeColorMap[typeKey], borderRadius: '50%'}}></div><span>{label}</span></span>
                                  <span className="badge bg-light ms-2">{count}</span>
                                </Nav.Link>
                              </Nav.Item>
                            );
                          })}
                        </Nav>
                      </Card.Body>
                    </Card>
                  
                  </Col>
   
                  {/* Right list column */}
                  <Col lg={10} md={9}>
                {assignedEvals.length === 0 ? (
                <Card className="shadow-sm border-0">
                  <Card.Body className="text-center d-flex flex-column align-items-center justify-content-center text-muted py-5">
                    <img src="/images/undraw_walking-outside_7jfy.svg" className="mb-4" style={{maxWidth: '300px'}} alt="No evaluations" />
                    <span className="fw-semibold">No Assigned Evaluations</span>
                    <p className="mb-0">
                      You don't have any assigned evaluations yet. <br />
                      Evaluations will appear here once they are assigned to you by your program.</p>
                  </Card.Body>
                </Card>
              ) : (
                    <Card className="shadow-sm border-0">
                      <CardHeader className="border-0 px-4 py-3">
                        <div className="d-flex flex-row align-items-center justify-content-between gap-3">
                          <div className="d-flex flex-column">
                            <h6 className="mb-0 fw-semibold">Assigned Evaluations</h6>
                            <p className="text-muted small mb-0">
                              Showing {filteredAssignedEvals.length} of {assignedEvals.length} evaluations
                            </p>
                          </div>
                          <Form.Group>
                          <Form.Control
                    type="search"
                    size="sm"
                    placeholder="Search evaluations..."
                    value={assignedSearch}
                    onChange={(e) => setAssignedSearch(e.target.value)}
                  />
                  </Form.Group>
                        </div>
                        </CardHeader>
                        <Card.Body className="p-3">
                      {filteredAssignedEvals.length === 0 ? (
                        <div className="text-center py-4 text-muted small">
                          No evaluations match your current filters.
                        </div>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {filteredAssignedEvals.map((evaluation) => {
                            const typeKey = getEvaluationTypeKey(evaluation) || 'template';
                            const isOverdue = isEvaluationOverdue(evaluation);
                            const getEvaluationIcon = (templateType) => {
                              switch (templateType?.toLowerCase()) {
                                case 'performance':
                                  return <i className="bi bi-bar-chart-fill text-primary" />;
                                case 'feedback':
                                  return <i className="bi bi-chat-dots-fill text-info" />;
                                case 'survey':
                                  return <i className="bi bi-clipboard-check-fill text-success" />;
                                case 'questionnaire':
                                  return <i className="bi bi-patch-question-fill text-warning" />;
                                default:
                                  return <i className="bi bi-clipboard-data text-muted" />;
                              }
                            };
                          
                            const getEvaluationIconBg = (templateType) => {
                              switch (templateType?.toLowerCase()) {
                                case 'performance':
                                  return 'var(--blue-light)';
                                case 'feedback':
                                  return 'var(--indigo-light)';
                                case 'survey':
                                  return 'var(--green-light)';
                                case 'questionnaire':
                                  return 'var(--yellow-light)';
                                default:
                                  return 'var(--gray-100)';
                              }
                            };
                            {/*const typeClass =
                              typeKey === 'performance'
                                ? 'bg-primary'
                                : typeKey === 'feedback'
                                ? 'bg-info'
                                : typeKey === 'survey'
                                ? 'bg-success'
                                : typeKey === 'questionnaire'
                                ? 'bg-warning'
                                : 'bg-secondary';
                            const typeLabel =
                              typeKey === 'performance'
                                ? 'Performance'
                                : typeKey === 'feedback'
                                ? 'Feedback'
                                : typeKey === 'survey'
                                ? 'Survey'
                                : typeKey === 'questionnaire'
                                ? 'Questionnaire'
                                : typeKey === 'template'
                                ? 'Template'
                                : 'Other';*/}

                            return (
                              <div
                                key={evaluation.id}
                                className="border-alt-light custom-hover-bg-alt rounded-3 p-3 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between"
                              >
                                <div className="d-flex flex-md-row flex-column align-items-md-center align-items-start justify-content-start gap-md-3 gap-1">
                                <div className="d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, backgroundColor: getEvaluationIconBg(evaluation.template?.type), borderRadius: 6 }}>
                                  {getEvaluationIcon(evaluation.template?.type)}
                                </div>
                                
                                <div className="d-flex flex-column gap-1">
                        
                                  <div className="d-flex flex-wrap align-items-center gap-2 ">
                                    <span className="fw-semibold">{evaluation.title}</span>
                                    {/* <span className={`badge rounded-pill ${typeClass}`}>
                                      {typeLabel}
                                    </span> */}

                                  </div>
                                  
                                  <div className="d-flex flex-wrap gap-md-3 gap-1 small text-muted">
                                    <span>
                                      <i className="bi bi-calendar-event me-1"></i>
                                      Start: {new Date(evaluation.startTime).toLocaleString()}
                                    </span>
                                    <span>
                                      <i className="bi bi-calendar2-event me-1"></i>
                                      End: {new Date(evaluation.endTime).toLocaleString()}
                                    </span>

                                  </div>
                                  </div>
                                  
                                </div>
                                
                                <div className="d-flex flex-row align-items-center justify-content-start gap-3">
                                  <Badge bg={evaluation.template?.isTimed ? 'warning' : 'secondary'} className="small rounded-3"> 
                                      <i className="bi bi-stopwatch me-1"></i>
                                      <span className="d-none d-md-inline">{evaluation.template?.isTimed ? 'Timed' : 'Not Timed'}</span>
                                    </Badge>
                                <span className={`badge rounded-pill ${isOverdue ? 'bg-danger' : 'bg-success'}`}>
                                      {isOverdue ? 'Overdue' : 'Active'}
                                    </span>
                                    <div className="d-flex w-100">
                               
                                  </div>   
                                  <Button
                                    variant={evaluation.progress ? 'outline-warning' : 'outline-success'}
                                    size="sm"
                                    onClick={() => {
                                      navigate(`/resident/evaluation/fill/${evaluation.id}`);
                                    }}
                                    style={{minWidth: '84px', width: 'auto'}}
                                  >
                                    {evaluation.progress ? 'Continue' : 'Start'}
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                    </Card.Body>
                    </Card>
                     )} 
                  </Col>
                
              </Row>
          </>
        )}

        {activeTab === 'completed' && (
          <Row className="g-3">
            {/* Left filters column */}
            <Col lg={2} md={3} className="d-none d-md-block">
              <Card className="shadow-sm border-0 mb-3 custom-sticky-top" style={{ top: '5rem' }}>
                <CardHeader className="border-0 px-3 py-3">
                  <h6 className="mb-0 fw-semibold">Type</h6>
                </CardHeader>
                <Card.Body className="pe-0 ps-2 pt-0 pb-3">
                  <Nav variant="pills" className="flex-column small">
                    <Nav.Item>
                      <Nav.Link
                        active={completedTypeFilter === 'all'}
                        onClick={() => setCompletedTypeFilter('all')}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span>All Types</span>
                        <span className="badge bg-light ms-2">{completedTabEvals.length}</span>
                      </Nav.Link>
                    </Nav.Item>
                    {(completedTypeCounts.survey || 0) > 0 && (
                      <Nav.Item>
                        <Nav.Link
                          active={completedTypeFilter === 'survey'}
                          onClick={() => setCompletedTypeFilter('survey')}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <span className="d-flex flex-row align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--bs-success)', borderRadius: '50%'}}></div> Survey</span>
                          <span className="badge bg-light ms-2">{completedTypeCounts.survey || 0}</span>
                        </Nav.Link>
                      </Nav.Item>
                    )}
                    {(completedTypeCounts.questionnaire || 0) > 0 && (
                      <Nav.Item>
                        <Nav.Link
                          active={completedTypeFilter === 'questionnaire'}
                          onClick={() => setCompletedTypeFilter('questionnaire')}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <span className="d-flex flex-row align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--bs-warning)', borderRadius: '50%'}}></div> Questionnaire</span>
                          <span className="badge bg-light ms-2">{completedTypeCounts.questionnaire || 0}</span>
                        </Nav.Link>
                      </Nav.Item>
                    )}
                  </Nav>
                </Card.Body>
              </Card>
            </Col>

            {/* Right list column */}
            <Col lg={10} md={9}>
              {completedTabEvals.length === 0 ? (
                <Card className="shadow-sm border-0">
                  <Card.Body className="text-center d-flex flex-column align-items-center justify-content-center text-muted py-5">
                    <img src="/images/undraw_walking-outside_7jfy.svg" className="mb-4" style={{maxWidth: '300px'}} alt="No evaluations" />
                    <span className="fw-semibold">No Completed Evaluations</span>
                    <p className="mb-0">
                      You don't have any completed evaluations yet. <br />
                      Evaluations will appear here once they are completed by your evaluators.
                    </p>
                  </Card.Body>
                </Card>
              ) : (
                <Card className="shadow-sm border-0">
                  <CardHeader className="border-0 px-4 py-3">
                    <div className="d-flex flex-row align-items-center justify-content-between gap-3">
                      <div className="d-flex flex-column">
                        <h6 className="mb-0 fw-semibold">Completed Evaluations</h6>
                        <p className="text-muted small mb-0">
                          Showing {filteredCompletedEvals.length} of {completedTabEvals.length} evaluations
                        </p>
                      </div>
                      <Form.Group>
                        <Form.Control
                          type="search"
                          size="sm"
                          placeholder="Search evaluations..."
                          value={completedSearch}
                          onChange={(e) => setCompletedSearch(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  </CardHeader>
                  <Card.Body className="p-3">
                    {filteredCompletedEvals.length === 0 ? (
                      <div className="text-center py-4 text-muted small">
                        No evaluations match your current filters.
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {filteredCompletedEvals.map((evaluation) => (
                          <div
                            key={evaluation.id}
                            className="border-alt-light custom-hover-bg-alt rounded-3 p-3 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between"
                          >
                            <div className="d-flex flex-md-row flex-column align-items-md-center align-items-start justify-content-start gap-md-3 gap-1">
                              <div className="d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, backgroundColor: getEvaluationIconBg(evaluation.evaluationType), borderRadius: 6 }}>
                                {getEvaluationIcon(evaluation.evaluationType)}
                              </div>
                              <div className="d-flex flex-column gap-1">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  <span className="fw-semibold">{evaluation.title}</span>
                                </div>
                                <div className="d-flex flex-wrap gap-md-3 gap-1 small text-muted">
                                  <span>
                                    <i className="bi bi-person me-1"></i>
                                    {evaluation.evaluator ? `${evaluation.evaluator.firstName || ''} ${evaluation.evaluator.lastName || ''}`.trim() || (evaluation.evaluator.role ? evaluation.evaluator.role.charAt(0).toUpperCase() + evaluation.evaluator.role.slice(1) : 'Unknown') : 'Self'}
                                  </span>
                                  <span>
                                    <i className="bi bi-calendar-check me-1"></i>
                                    {evaluation.submittedAt ? new Date(evaluation.submittedAt).toLocaleString() : "Date not available"}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="d-flex flex-row align-items-center justify-content-start gap-3">
                              <Badge bg={evaluation.evaluationType === 'survey' ? 'success' : 'warning'} className="small rounded-3">
                                {evaluation.evaluationType === 'survey' ? 'Survey' : 'Questionnaire'}
                              </Badge>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => navigate(`/resident/completed-evaluation/${evaluation.id}`)}
                                style={{minWidth: '84px', width: 'auto'}}
                              >
                                View Response
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        )}

        {activeTab === 'performance' && (
          <Row className="g-3">
            {/* Left filters column */}
            <Col lg={2} md={3} className="d-none d-md-block">
              <Card className="shadow-sm border-0 mb-3 custom-sticky-top" style={{ top: '5rem' }}>
                <CardHeader className="border-0 px-3 py-3">
                  <h6 className="mb-0 fw-semibold">Performance</h6>
                </CardHeader>
                <Card.Body className="pe-0 ps-2 pt-0 pb-3">
                  <Nav variant="pills" className="flex-column small">
                    <Nav.Item>
                      <Nav.Link
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span className="d-flex flex-row align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--bs-primary)', borderRadius: '50%'}}></div> All</span>
                        <span className="badge bg-light ms-2">{performanceTabEvals.length}</span>
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Card.Body>
              </Card>
            </Col>

            {/* Right list column */}
            <Col lg={10} md={9}>
              {performanceTabEvals.length === 0 ? (
                <Card className="shadow-sm border-0">
                  <Card.Body className="text-center d-flex flex-column align-items-center justify-content-center text-muted py-5">
                    <img src="/images/undraw_walking-outside_7jfy.svg" className="mb-4" style={{maxWidth: '300px'}} alt="No evaluations" />
                    <span className="fw-semibold">No Performance Evaluations</span>
                    <p className="mb-0">
                      You don't have any completed performance evaluations yet. <br />
                      Performance evaluations will appear here once they are completed by your evaluators.
                    </p>
                  </Card.Body>
                </Card>
              ) : (
                <Card className="shadow-sm border-0">
                  <CardHeader className="border-0 px-4 py-3">
                    <div className="d-flex flex-row align-items-center justify-content-between gap-3">
                      <div className="d-flex flex-column">
                        <h6 className="mb-0 fw-semibold">Performance Evaluations</h6>
                        <p className="text-muted small mb-0">
                          Showing {filteredPerformanceEvals.length} of {performanceTabEvals.length} evaluations
                        </p>
                      </div>
                      <Form.Group>
                        <Form.Control
                          type="search"
                          size="sm"
                          placeholder="Search evaluations..."
                          value={performanceSearch}
                          onChange={(e) => setPerformanceSearch(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  </CardHeader>
                  <Card.Body className="p-3">
                    {filteredPerformanceEvals.length === 0 ? (
                      <div className="text-center py-4 text-muted small">
                        No evaluations match your current filters.
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {filteredPerformanceEvals.map((evaluation) => {
                          const evaluator = evaluation.submittedBy || evaluation.evaluator;
                          const evaluatorName = evaluator ? `${evaluator.firstName || ''} ${evaluator.lastName || ''}`.trim() : null;
                          const evaluatorRole = evaluator?.role;
                          return (
                            <div
                              key={evaluation.id}
                              className="border-alt-light custom-hover-bg-alt rounded-3 p-3 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between"
                            >
                              <div className="d-flex flex-md-row flex-column align-items-md-center align-items-start justify-content-start gap-md-3 gap-1">
                                <div className="d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, backgroundColor: getEvaluationIconBg('performance'), borderRadius: 6 }}>
                                  {getEvaluationIcon('performance')}
                                </div>
                                <div className="d-flex flex-column gap-1">
                                  <div className="d-flex flex-wrap align-items-center gap-2">
                                    <span className="fw-semibold">{evaluation.title}</span>
                                  </div>
                                  <div className="d-flex flex-wrap gap-md-3 gap-1 small text-muted">
                                    <span className="d-flex align-items-center gap-2">
                                      <i className="bi bi-person me-1"></i>
                                      {evaluatorName || (evaluatorRole ? evaluatorRole.charAt(0).toUpperCase() + evaluatorRole.slice(1) : 'Not available')}
                                      {evaluatorRole && <Badge bg="secondary" className="small">{evaluatorRole}</Badge>}
                                    </span>
                                    <span>
                                      <i className="bi bi-calendar-check me-1"></i>
                                      {evaluation.submittedAt ? new Date(evaluation.submittedAt).toLocaleString() : "Date not available"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex flex-row align-items-center justify-content-start gap-3">
                                <Badge bg="primary" className="small rounded-3">Performance</Badge>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => navigate(`/resident/completed-evaluation/${evaluation.id}`)}
                                  style={{minWidth: '84px', width: 'auto'}}
                                >
                                  View Response
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        )}

        {activeTab === 'feedback' && (
          <Row className="g-3">
            {/* Left filters column */}
            <Col lg={2} md={3} className="d-none d-md-block">
              <Card className="shadow-sm border-0 mb-3 custom-sticky-top" style={{ top: '5rem' }}>
                <CardHeader className="border-0 px-3 py-3">
                  <h6 className="mb-0 fw-semibold">Feedback</h6>
                </CardHeader>
                <Card.Body className="pe-0 ps-2 pt-0 pb-3">
                  <Nav variant="pills" className="flex-column small">
                    <Nav.Item>
                      <Nav.Link
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span className="d-flex flex-row align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--bs-info)', borderRadius: '50%'}}></div> All</span>
                        <span className="badge bg-light ms-2">{feedbackTabEvals.length}</span>
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Card.Body>
              </Card>
            </Col>

            {/* Right list column */}
            <Col lg={10} md={9}>
              {feedbackTabEvals.length === 0 ? (
                <Card className="shadow-sm border-0">
                  <Card.Body className="text-center d-flex flex-column align-items-center justify-content-center text-muted py-5">
                    <img src="/images/undraw_walking-outside_7jfy.svg" className="mb-4" style={{maxWidth: '300px'}} alt="No evaluations" />
                    <span className="fw-semibold">No Feedback Evaluations</span> 
                    <p className="mb-0">
                      You don't have any completed feedback evaluations yet. <br />
                      Feedback evaluations will appear here once they are completed by your evaluators.
                    </p>
                  </Card.Body>
                </Card>
              ) : (
                <Card className="shadow-sm border-0">
                  <CardHeader className="border-0 px-4 py-3">
                    <div className="d-flex flex-row align-items-center justify-content-between gap-3">
                      <div className="d-flex flex-column">
                        <h6 className="mb-0 fw-semibold">Feedback Evaluations</h6>
                        <p className="text-muted small mb-0">
                          Showing {filteredFeedbackEvals.length} of {feedbackTabEvals.length} evaluations
                        </p>
                      </div>
                      <Form.Group>
                        <Form.Control
                          type="search"
                          size="sm"
                          placeholder="Search evaluations..."
                          value={feedbackSearch}
                          onChange={(e) => setFeedbackSearch(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  </CardHeader>
                  <Card.Body className="p-3">
                    {filteredFeedbackEvals.length === 0 ? (
                      <div className="text-center py-4 text-muted small">
                        No evaluations match your current filters.
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {filteredFeedbackEvals.map((evaluation) => {
                          const evaluator = evaluation.submittedBy || evaluation.evaluator;
                          const evaluatorName = evaluator ? `${evaluator.firstName || ''} ${evaluator.lastName || ''}`.trim() : null;
                          const evaluatorRole = evaluator?.role;
                          return (
                            <div
                              key={evaluation.id}
                              className="border-alt-light custom-hover-bg-alt rounded-3 p-3 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between"
                            >
                              <div className="d-flex flex-md-row flex-column align-items-md-center align-items-start justify-content-start gap-md-3 gap-1">
                                <div className="d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, backgroundColor: getEvaluationIconBg('feedback'), borderRadius: 6 }}>
                                  {getEvaluationIcon('feedback')}
                                </div>
                                <div className="d-flex flex-column gap-1">
                                  <div className="d-flex flex-wrap align-items-center gap-2">
                                    <span className="fw-semibold">{evaluation.title}</span>
                                  </div>
                                  <div className="d-flex flex-wrap gap-md-3 gap-1 small text-muted">
                                    <span className="d-flex align-items-center gap-2">
                                      <i className="bi bi-person me-1"></i>
                                      {evaluatorName || (evaluatorRole ? evaluatorRole.charAt(0).toUpperCase() + evaluatorRole.slice(1) : 'Not available')}
                                      {evaluatorRole && <Badge bg="secondary" className="small">{evaluatorRole}</Badge>}
                                    </span>
                                    <span>
                                      <i className="bi bi-calendar-check me-1"></i>
                                      {evaluation.submittedAt ? new Date(evaluation.submittedAt).toLocaleString() : "Date not available"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex flex-row align-items-center justify-content-start gap-3">
                                <Badge bg="info" className="small rounded-3">Feedback</Badge>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => navigate(`/resident/completed-evaluation/${evaluation.id}`)}
                                  style={{minWidth: '84px', width: 'auto'}}
                                >
                                  View Response
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        )}

      </Container>
    </div>
  );
};

export default ResidentEvaluations;
